import MatchResultsAutomationStatus from './MatchResultsAutomationStatus';
import MatchResultsAcquisitionPilot from './MatchResultsAcquisitionPilot';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import { createAndDownloadBackup } from '../services/backupService';
import type { AppSettings as AppSettingsType, LookupState, TimeFormat } from '../types';
import { pairingCompatibilityAnalyticsEnabled } from '../utils/featureFlags';
import { runPairingCompatibilityBackfill, type PairingCompatibilityBackfillSummary } from '../utils/pairingCompatibilityBackfill';
import { defaultPairingCompatibilityConfig, type PairingCalculationConfig } from '../utils/pairingCompatibilityAnalytics';
import {
  loadPairingCompatibilityConfig,
  normalizePairingCompatibilityConfig,
  pairingCompatibilityWeightTotal,
  savePairingCompatibilityConfig,
} from '../utils/pairingCompatibilitySettings';
import { supabase } from '../config/supabase';
import { useAuditUserId } from '../auth/AuthContext';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';

type AppSettingsProps = {
  appSettings: AppSettingsType;
  onSave: (settings: AppSettingsType) => Promise<void>;
  lookups: LookupState;
  isAdmin: boolean;
  exportedBy: string;
  playerPortalEnabled: boolean;
  readOnly?: boolean;
};

type PartnerCompatibilityRecalculationViewState = {
  suppressNotifications: boolean;
};

type Snack = {
  message: string;
  severity: 'success' | 'warning' | 'error';
};

type McpAccessToken = {
  id: string;
  name: string;
  scopes: string[];
  tokenPrefix: string | null;
  tokenLast4: string | null;
  createdAt: string | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  active: boolean;
};

type GmailStatus = {
  configured: boolean;
  connected: boolean;
  email: string | null;
  connectedAt: string | null;
  lastSuccessfulUseAt: string | null;
  lastError: string | null;
  missingConfigKeys?: string[];
};

const lastBackupStorageKey = 'courtcaptain:last-backup-exported-at';
const weightFields: Array<{ key: keyof PairingCalculationConfig['weights']; label: string; helper: string }> = [
  { key: 'results', label: 'Results', helper: 'Overall win/loss record together.' },
  { key: 'dominance', label: 'Dominance', helper: 'Set and game strength.' },
  { key: 'adaptability', label: 'Adaptability', helper: 'Comebacks and adjustment after losing Set 1.' },
  { key: 'pressurePerformance', label: 'Pressure', helper: 'Match-tiebreak and deciding-moment performance.' },
  { key: 'closingPerformance', label: 'Closing', helper: 'Protecting leads after winning Set 1.' },
  { key: 'consistency', label: 'Consistency', helper: 'Stable match-to-match performance.' },
  { key: 'synergy', label: 'Synergy', helper: 'Improvement and combined momentum signals.' },
  { key: 'lineContext', label: 'Line Context', helper: 'Credit for success on higher lines.' },
];

function formatLastBackup(value: string | null): string {
  if (!value) return 'No backup has been downloaded on this device.';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No backup has been downloaded on this device.';
  return `Last backup downloaded from this device: ${date.toLocaleString()}`;
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function emptyAggregateSummary(): PairingCompatibilityBackfillSummary {
  return {
    workspaceId: 'all',
    completedDoublesLines: 0,
    pairingsEvaluated: 0,
    ratingsEstablished: 0,
    ratingsUpdated: 0,
    reviewFlags: 0,
    skippedInsufficientEvidence: 0,
    incompleteScoreLines: 0,
    diagnostics: {
      workspaceTeams: 0,
      candidateMatches: 0,
      matchResultsLoaded: 0,
      eligibleMatches: 0,
      doublesPositionsLoaded: 0,
      lineResultsLoaded: 0,
      lineupEntriesLoaded: 0,
      playableDoublesLines: 0,
      defaultedLinesSkipped: 0,
      linesWithoutTwoPlayers: 0,
    },
    errors: [],
  };
}

function mergeSummary(target: PairingCompatibilityBackfillSummary, source: PairingCompatibilityBackfillSummary, workspaceLabel: string): void {
  target.completedDoublesLines += source.completedDoublesLines;
  target.pairingsEvaluated += source.pairingsEvaluated;
  target.ratingsEstablished += source.ratingsEstablished;
  target.ratingsUpdated += source.ratingsUpdated;
  target.reviewFlags += source.reviewFlags;
  target.skippedInsufficientEvidence += source.skippedInsufficientEvidence;
  target.incompleteScoreLines += source.incompleteScoreLines;
  target.diagnostics.workspaceTeams += source.diagnostics.workspaceTeams;
  target.diagnostics.candidateMatches += source.diagnostics.candidateMatches;
  target.diagnostics.matchResultsLoaded += source.diagnostics.matchResultsLoaded;
  target.diagnostics.eligibleMatches += source.diagnostics.eligibleMatches;
  target.diagnostics.doublesPositionsLoaded += source.diagnostics.doublesPositionsLoaded;
  target.diagnostics.lineResultsLoaded += source.diagnostics.lineResultsLoaded;
  target.diagnostics.lineupEntriesLoaded += source.diagnostics.lineupEntriesLoaded;
  target.diagnostics.playableDoublesLines += source.diagnostics.playableDoublesLines;
  target.diagnostics.defaultedLinesSkipped += source.diagnostics.defaultedLinesSkipped;
  target.diagnostics.linesWithoutTwoPlayers += source.diagnostics.linesWithoutTwoPlayers;
  source.errors.forEach((error) => target.errors.push(`${workspaceLabel}: ${error}`));
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 800 }}>{value}</Typography>
    </Paper>
  );
}

async function authHeaders(): Promise<Record<string, string>> {
  const session = (await supabase.auth.getSession()).data.session;
  return session ? { Authorization: `Bearer ${session.access_token}` } : {};
}

export function AppSettings({ appSettings, onSave, lookups, isAdmin, exportedBy, playerPortalEnabled, readOnly = false }: AppSettingsProps) {
  const auditUserId = useAuditUserId();
  const pairingCompatibilityEnabled = pairingCompatibilityAnalyticsEnabled();
  const [timeFormat, setTimeFormat] = useState<TimeFormat>(appSettings.timeFormat);
  const [playerEventSelfServiceEnabled, setPlayerEventSelfServiceEnabled] = useState(Boolean(appSettings.playerEventSelfServiceEnabled));
  const [playerCalendarEventsEnabled, setPlayerCalendarEventsEnabled] = useState(Boolean(appSettings.playerCalendarEventsEnabled));
  const [pairingConfig, setPairingConfig] = useState<PairingCalculationConfig>(defaultPairingCompatibilityConfig);
  const [saving, setSaving] = useState(false);
  const [savingPairingConfig, setSavingPairingConfig] = useState(false);
  const [loadingPairingConfig, setLoadingPairingConfig] = useState(false);
  const {
    state: recalculationViewState,
    setState: setRecalculationViewState,
    stateKey: recalculationViewStateKey,
    loadedStateKey: loadedRecalculationViewStateKey,
  } = usePersistentViewState<PartnerCompatibilityRecalculationViewState>({
    userId: auditUserId,
    routeKey: 'app-settings:partner-compatibility',
    defaultState: { suppressNotifications: true },
    validate: (state): state is PartnerCompatibilityRecalculationViewState => Boolean(
      state && typeof state === 'object' && typeof (state as PartnerCompatibilityRecalculationViewState).suppressNotifications === 'boolean',
    ),
  });
  const [suppressRecalculationNotifications, setSuppressRecalculationNotifications] = useState(recalculationViewState.suppressNotifications);
  const [recalculationViewStateReady, setRecalculationViewStateReady] = useState(false);
  const restoredRecalculationViewStateKey = useRef('');
  const [recalculatingCompatibility, setRecalculatingCompatibility] = useState(false);
  const [recalculationConfirmOpen, setRecalculationConfirmOpen] = useState(false);
  const [recalculationSummary, setRecalculationSummary] = useState<PairingCompatibilityBackfillSummary | null>(null);
  const [lastRecalculatedAt, setLastRecalculatedAt] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [lastBackupAt, setLastBackupAt] = useState<string | null>(() => localStorage.getItem(lastBackupStorageKey));
  const [snack, setSnack] = useState<Snack | null>(null);
  const [mcpTokens, setMcpTokens] = useState<McpAccessToken[]>([]);
  const [mcpTokensLoading, setMcpTokensLoading] = useState(false);
  const [mcpTokenSaving, setMcpTokenSaving] = useState(false);
  const [mcpTokenName, setMcpTokenName] = useState('Hermes Agent');
  const [mcpTokenExpiresAt, setMcpTokenExpiresAt] = useState('');
  const [newMcpToken, setNewMcpToken] = useState<string | null>(null);
  const [gmailStatus, setGmailStatus] = useState<GmailStatus | null>(null);
  const [gmailLoading, setGmailLoading] = useState(false);
  const [gmailSaving, setGmailSaving] = useState(false);

  usePersistentScrollRestoration({
    userId: auditUserId,
    routeKey: 'app-settings',
    ready: !saving && !savingPairingConfig && !loadingPairingConfig && !recalculatingCompatibility && !mcpTokensLoading && !mcpTokenSaving && !gmailLoading && !gmailSaving,
  });

  useEffect(() => {
    setTimeFormat(appSettings.timeFormat);
    setPlayerEventSelfServiceEnabled(Boolean(appSettings.playerEventSelfServiceEnabled));
    setPlayerCalendarEventsEnabled(Boolean(appSettings.playerCalendarEventsEnabled));
  }, [appSettings.playerCalendarEventsEnabled, appSettings.playerEventSelfServiceEnabled, appSettings.timeFormat]);

  useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      try {
        const response = await fetch('/api/app-settings/player-features', { headers: await authHeaders() });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) return;
        setPlayerEventSelfServiceEnabled(Boolean(payload.playerEventSelfServiceEnabled));
        setPlayerCalendarEventsEnabled(Boolean(payload.playerCalendarEventsEnabled));
      } catch {
        // Keep default-off local state if the runtime settings endpoint is unavailable.
      }
    })();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || !pairingCompatibilityEnabled) return;
    setLoadingPairingConfig(true);
    void loadPairingCompatibilityConfig()
      .then(setPairingConfig)
      .finally(() => setLoadingPairingConfig(false));
  }, [isAdmin, pairingCompatibilityEnabled]);

  useEffect(() => {
    if (!auditUserId || loadedRecalculationViewStateKey !== recalculationViewStateKey) {
      setRecalculationViewStateReady(false);
      return;
    }
    if (restoredRecalculationViewStateKey.current === recalculationViewStateKey) {
      setRecalculationViewStateReady(true);
      return;
    }
    setSuppressRecalculationNotifications(recalculationViewState.suppressNotifications);
    restoredRecalculationViewStateKey.current = recalculationViewStateKey;
    setRecalculationViewStateReady(true);
  }, [auditUserId, loadedRecalculationViewStateKey, recalculationViewState, recalculationViewStateKey]);

  useEffect(() => {
    if (!recalculationViewStateReady || restoredRecalculationViewStateKey.current !== recalculationViewStateKey) return;
    setRecalculationViewState({ suppressNotifications: suppressRecalculationNotifications });
  }, [recalculationViewStateKey, recalculationViewStateReady, setRecalculationViewState, suppressRecalculationNotifications]);

  useEffect(() => {
    if (window.location.hash !== '#partner-compatibility') return;
    window.setTimeout(() => {
      document.getElementById('partner-compatibility')?.scrollIntoView({ block: 'start' });
    }, 0);
  }, []);

  const activeCompatibilityWorkspaces = useMemo(
    () => lookups.workspaces.filter((workspace) => lookups.teams.some((team) => team.workspace_id === workspace.value)),
    [lookups.teams, lookups.workspaces],
  );
  const compatibilityScopedTeams = useMemo(
    () => lookups.teams.filter((team) => activeCompatibilityWorkspaces.some((workspace) => workspace.value === team.workspace_id)),
    [activeCompatibilityWorkspaces, lookups.teams],
  );
  const latestSeasonId = lookups.seasons[0]?.value || null;
  const recalculationScopeText = `${compatibilityScopedTeams.length} team${compatibilityScopedTeams.length === 1 ? '' : 's'} • ${activeCompatibilityWorkspaces.length} workspace${activeCompatibilityWorkspaces.length === 1 ? '' : 's'}`;

  const loadMcpTokens = async () => {
    setMcpTokensLoading(true);
    try {
      const response = await fetch('/api/mcp-tokens', {
        headers: await authHeaders(),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Unable to load MCP access tokens.');
      setMcpTokens(Array.isArray(payload.tokens) ? payload.tokens : []);
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to load MCP access tokens.' });
    } finally {
      setMcpTokensLoading(false);
    }
  };

  const loadGmailStatus = async () => {
    setGmailLoading(true);
    try {
      const response = await fetch('/api/gmail/status', {
        headers: await authHeaders(),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Unable to load Gmail connection.');
      setGmailStatus(payload as GmailStatus);
    } catch (error) {
      setGmailStatus(null);
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to load Gmail connection.' });
    } finally {
      setGmailLoading(false);
    }
  };

  useEffect(() => {
    void loadMcpTokens();
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setGmailStatus(null);
      return;
    }
    void loadGmailStatus();
  }, [isAdmin]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gmailResult = params.get('gmail');
    if (!gmailResult) return;
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.delete('gmail');
    window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    if (!isAdmin) return;
    if (gmailResult === 'connected') {
      setSnack({ severity: 'success', message: 'Gmail connected.' });
      void loadGmailStatus();
    } else {
      setSnack({ severity: 'error', message: 'Gmail connection was not completed.' });
    }
  }, [isAdmin]);

  const connectGmail = async () => {
    setGmailSaving(true);
    try {
      const response = await fetch('/api/gmail/oauth/start', {
        method: 'POST',
        headers: await authHeaders(),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.authorizationUrl) throw new Error(payload.error || 'Unable to start Gmail connection.');
      window.location.assign(payload.authorizationUrl);
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to start Gmail connection.' });
      setGmailSaving(false);
    }
  };

  const disconnectGmail = async () => {
    if (!window.confirm('Disconnect Gmail from CourtCaptain?')) return;
    setGmailSaving(true);
    try {
      const response = await fetch('/api/gmail/disconnect', {
        method: 'POST',
        headers: await authHeaders(),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Unable to disconnect Gmail.');
      setSnack({ severity: 'success', message: 'Gmail disconnected.' });
      await loadGmailStatus();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to disconnect Gmail.' });
    } finally {
      setGmailSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isAdmin && !readOnly) {
        const response = await fetch('/api/app-settings/player-features', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(await authHeaders()),
          },
          body: JSON.stringify({
            playerEventSelfServiceEnabled,
            playerCalendarEventsEnabled,
          }),
        });
        const result = await response.json().catch(() => ({})) as { error?: string };
        if (!response.ok) throw new Error(result.error || 'Unable to save Player Portal settings.');
      }
      // The global time-format setting predates the admin settings API and uses
      // its own database write. Avoid attempting that legacy write when it has
      // not changed: it can otherwise prevent unrelated Player Portal settings
      // from being saved by an otherwise-authorized administrator.
      if (timeFormat !== appSettings.timeFormat) await onSave({ timeFormat });
      setSnack({ severity: 'success', message: 'App settings saved.' });
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to save app settings.' });
    } finally {
      setSaving(false);
    }
  };

  const updateWeight = (key: keyof PairingCalculationConfig['weights'], value: string) => {
    setPairingConfig((current) => normalizePairingCompatibilityConfig({
      ...current,
      weights: { ...current.weights, [key]: value },
    }));
  };

  const updateThreshold = (key: keyof PairingCalculationConfig['thresholds'], value: string) => {
    setPairingConfig((current) => normalizePairingCompatibilityConfig({
      ...current,
      thresholds: { ...current.thresholds, [key]: value },
    }));
  };

  const updateEvidence = (key: keyof PairingCalculationConfig['evidenceThresholds'], value: string) => {
    setPairingConfig((current) => normalizePairingCompatibilityConfig({
      ...current,
      evidenceThresholds: { ...current.evidenceThresholds, [key]: value },
    }));
  };

  const updateSeasonWeight = (key: 'currentSeasonWeight' | 'priorSeasonWeight' | 'olderSeasonWeight', value: string) => {
    setPairingConfig((current) => normalizePairingCompatibilityConfig({ ...current, [key]: value }));
  };

  const savePairingConfig = async () => {
    const normalized = normalizePairingCompatibilityConfig(pairingConfig);
    if (pairingCompatibilityWeightTotal(normalized) <= 0) {
      setSnack({ severity: 'error', message: 'At least one pairing compatibility weight must be greater than zero.' });
      return;
    }
    setSavingPairingConfig(true);
    try {
      const saved = await savePairingCompatibilityConfig(normalized);
      setPairingConfig(saved);
      setSnack({ severity: 'success', message: 'Pairing compatibility settings saved. Recalculate compatibility scores to apply changes to historical pairings.' });
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to save pairing compatibility settings.' });
    } finally {
      setSavingPairingConfig(false);
    }
  };

  const runCompatibilityRecalculation = async () => {
    if (!isAdmin || readOnly) {
      setSnack({ severity: 'error', message: 'Only administrators can recalculate compatibility scores.' });
      return;
    }
    if (activeCompatibilityWorkspaces.length === 0) {
      setSnack({ severity: 'error', message: 'No active workspaces with teams are available for compatibility recalculation.' });
      return;
    }
    setRecalculationConfirmOpen(false);
    setRecalculatingCompatibility(true);
    setRecalculationSummary(null);
    const result = emptyAggregateSummary();
    try {
      for (const workspace of activeCompatibilityWorkspaces) {
        const workspaceResult = await runPairingCompatibilityBackfill(supabase, {
          workspaceId: workspace.value,
          currentSeasonId: latestSeasonId,
          suppressNotifications: suppressRecalculationNotifications,
          auditUserId,
        });
        mergeSummary(result, workspaceResult, workspace.label);
      }
      setRecalculationSummary(result);
      setLastRecalculatedAt(new Date().toISOString());
      if (result.errors.length > 0) {
        setSnack({ severity: 'error', message: result.errors.join('\n') });
        return;
      }
      const updatedPairings = result.ratingsEstablished + result.ratingsUpdated;
      setSnack({
        severity: 'success',
        message: `Compatibility scores recalculated successfully. ${updatedPairings} pairing${updatedPairings === 1 ? '' : 's'} updated from ${result.diagnostics.eligibleMatches} completed match${result.diagnostics.eligibleMatches === 1 ? '' : 'es'}.`,
      });
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to recalculate compatibility scores.' });
    } finally {
      setRecalculatingCompatibility(false);
    }
  };

  const downloadBackup = async () => {
    setExporting(true);
    try {
      const result = await createAndDownloadBackup({ exportedBy, isAdmin });
      const exportedAt = result.manifest.exportedAt;
      localStorage.setItem(lastBackupStorageKey, exportedAt);
      setLastBackupAt(exportedAt);

      const tableText = `${result.manifest.tableCount} ${result.manifest.tableCount === 1 ? 'table' : 'tables'} exported.`;
      if (result.manifest.failures.length > 0) {
        const failureText = `${result.manifest.failures.length} ${result.manifest.failures.length === 1 ? 'table failed' : 'tables failed'}.`;
        setSnack({ severity: 'warning', message: `Backup completed with warnings. ${tableText} ${failureText}` });
      } else {
        setSnack({ severity: 'success', message: `Backup created successfully. ${tableText}` });
      }
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to create backup.' });
    } finally {
      setExporting(false);
    }
  };

  const createMcpToken = async () => {
    if (!mcpTokenName.trim()) {
      setSnack({ severity: 'error', message: 'Enter a token name.' });
      return;
    }
    setMcpTokenSaving(true);
    try {
      const expiresAt = mcpTokenExpiresAt ? new Date(`${mcpTokenExpiresAt}T23:59:59`).toISOString() : null;
      const response = await fetch('/api/mcp-tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await authHeaders()),
        },
        body: JSON.stringify({ action: 'create', name: mcpTokenName.trim(), expiresAt }),
      });
      const responseText = await response.text();
      let payload: any = {};
      try {
        payload = responseText ? JSON.parse(responseText) : {};
      } catch {
        payload = {};
      }
      if (!response.ok) {
        const detail = payload.error || responseText || response.statusText || 'No response detail returned.';
        throw new Error(`Unable to create MCP access token. HTTP ${response.status}. ${String(detail).slice(0, 900)}`);
      }
      setNewMcpToken(payload.plaintextToken || null);
      setSnack({ severity: 'success', message: 'MCP access token created. Copy it now; it will not be shown again.' });
      await loadMcpTokens();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to create MCP access token.' });
    } finally {
      setMcpTokenSaving(false);
    }
  };

  const revokeMcpToken = async (tokenId: string) => {
    if (!window.confirm('Revoke this MCP access token? Hermes will stop working with this token immediately.')) return;
    setMcpTokenSaving(true);
    try {
      const response = await fetch('/api/mcp-tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await authHeaders()),
        },
        body: JSON.stringify({ action: 'revoke', tokenId }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Unable to revoke MCP access token.');
      setSnack({ severity: 'success', message: 'MCP access token revoked.' });
      await loadMcpTokens();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to revoke MCP access token.' });
    } finally {
      setMcpTokenSaving(false);
    }
  };

  const copyNewMcpToken = async () => {
    if (!newMcpToken) return;
    await navigator.clipboard.writeText(newMcpToken);
    setSnack({ severity: 'success', message: 'MCP token copied.' });
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        App Settings
      </Typography>
      {isAdmin && <><MatchResultsAutomationStatus /><MatchResultsAcquisitionPilot disabled={readOnly} /></>}

      <Paper variant="outlined" sx={{ p: 2, maxWidth: 520 }}>
        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Time Format</InputLabel>
            <Select label="Time Format" value={timeFormat} onChange={(event) => setTimeFormat(event.target.value as TimeFormat)}>
              <MenuItem value="12h">12-hour AM/PM</MenuItem>
              <MenuItem value="24h">24-hour</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={() => void save()} disabled={saving}>
            Save Settings
          </Button>
        </Stack>
      </Paper>

      {isAdmin && (
        <Paper variant="outlined" sx={{ p: 2, maxWidth: 720 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Player Portal</Typography>
                <Typography variant="body2" color="text.secondary">
                  Controlled first by the server-side PLAYER_PORTAL_ENABLED setting.
                </Typography>
              </Box>
              <Alert severity={playerPortalEnabled ? 'success' : 'info'} sx={{ py: 0 }}>
                {playerPortalEnabled ? 'Enabled' : 'Disabled'}
              </Alert>
            </Stack>
            <Divider />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Player Event Self-Service</Typography>
              <Typography variant="body2" color="text.secondary">
                Player Event Self-Service lets eligible teams allow players to update availability and attendance directly in CourtCaptain.
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={playerEventSelfServiceEnabled} onChange={(event) => setPlayerEventSelfServiceEnabled(event.target.checked)} disabled={readOnly || !playerPortalEnabled} />}
                label="Enable Player Event Self-Service"
              />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Player Calendar Events</Typography>
              <Typography variant="body2" color="text.secondary">
                Allows all active users with linked player profiles to email themselves calendar invitations and updates.
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={playerCalendarEventsEnabled} onChange={(event) => setPlayerCalendarEventsEnabled(event.target.checked)} disabled={readOnly || !playerPortalEnabled} />}
                label="Enable Player Calendar Events"
              />
            </Box>
          </Stack>
        </Paper>
      )}

      {isAdmin && (
      <Paper variant="outlined" sx={{ p: 2, maxWidth: 720 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Integrations</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Connect Gmail to send opposing captain messages from your own account.
              </Typography>
            </Box>
            <Chip
              icon={<EmailIcon />}
              label={gmailStatus?.connected ? 'Gmail Connected' : 'Gmail Not Connected'}
              color={gmailStatus?.connected ? 'success' : 'default'}
            />
          </Stack>
          <Divider />
          {gmailLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading Gmail connection…</Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                {gmailStatus?.configured === false
                  ? gmailStatus.missingConfigKeys?.length
                    ? `Gmail is missing staging settings: ${gmailStatus.missingConfigKeys.join(', ')}.`
                    : 'Gmail is not configured for this environment.'
                  : gmailStatus?.connected && gmailStatus.email
                  ? `Connected as ${gmailStatus.email}.`
                  : 'No Gmail account is connected.'}
              </Typography>
              {gmailStatus?.lastSuccessfulUseAt && (
                <Typography variant="caption" color="text.secondary">
                  Last sent from CourtCaptain: {formatDateTime(gmailStatus.lastSuccessfulUseAt)}
                </Typography>
              )}
              {gmailStatus?.lastError && <Alert severity="warning">{gmailStatus.lastError}</Alert>}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="contained" startIcon={<EmailIcon />} onClick={() => void connectGmail()} disabled={gmailSaving || readOnly || gmailStatus?.configured === false}>
                  {gmailStatus?.connected ? 'Reconnect Gmail' : 'Connect Gmail'}
                </Button>
                {gmailStatus?.connected && (
                  <Button color="error" startIcon={<DeleteIcon />} onClick={() => void disconnectGmail()} disabled={gmailSaving || readOnly}>
                    Disconnect
                  </Button>
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, maxWidth: 920 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Developer / API Access</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Create read-only MCP tokens for private agents such as Hermes. Tokens are shown once, stored hashed, and can be revoked at any time.
            </Typography>
          </Box>
          <Divider />
          {newMcpToken && (
            <Alert
              severity="warning"
              action={(
                <Button color="inherit" size="small" startIcon={<ContentCopyIcon />} onClick={() => void copyNewMcpToken()}>
                  Copy
                </Button>
              )}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Copy this token now. It will not be shown again.</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 0.5 }}>{newMcpToken}</Typography>
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr auto' }, gap: 1.5, alignItems: 'center' }}>
            <TextField label="Token Name" value={mcpTokenName} onChange={(event) => setMcpTokenName(event.target.value)} disabled={mcpTokenSaving} />
            <TextField
              label="Expiration Date"
              type="date"
              value={mcpTokenExpiresAt}
              onChange={(event) => setMcpTokenExpiresAt(event.target.value)}
              disabled={mcpTokenSaving}
              InputLabelProps={{ shrink: true }}
              helperText="Optional"
            />
            <Button variant="contained" onClick={() => void createMcpToken()} disabled={mcpTokenSaving}>
              Create Token
            </Button>
          </Box>
          <Divider />
          {mcpTokensLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading MCP tokens…</Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {mcpTokens.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No MCP access tokens have been created.</Typography>
              ) : mcpTokens.map((token) => (
                <Paper key={token.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{token.name}</Typography>
                        <Chip size="small" label={token.active ? 'Active' : 'Revoked / Expired'} color={token.active ? 'success' : 'default'} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Token: {token.tokenPrefix || 'cc_mcp_'}…{token.tokenLast4 || '----'} • Scope: {(token.scopes || []).join(', ') || 'mcp:read'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Created {formatDateTime(token.createdAt)} • Last used {formatDateTime(token.lastUsedAt)} • Expires {formatDateTime(token.expiresAt)}
                      </Typography>
                    </Box>
                    <Button
                      color="error"
                      startIcon={<DeleteIcon />}
                      disabled={!token.active || mcpTokenSaving}
                      onClick={() => void revokeMcpToken(token.id)}
                    >
                      Revoke
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      {isAdmin && pairingCompatibilityEnabled && (
        <Paper id="partner-compatibility" variant="outlined" sx={{ p: 2, maxWidth: 920 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Partner Compatibility</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Configure how CourtCaptain rates doubles pairings and recalculate those data-driven ratings from completed match history.
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Pairing Compatibility Scoring</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Tune how match history becomes Poor, Neutral, Good, and Excellent compatibility ratings. Changes apply the next time compatibility scores are recalculated.
              </Typography>
            </Box>
            {loadingPairingConfig ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Loading pairing settings…</Typography>
              </Box>
            ) : (
              <>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Compatibility Score Ranges</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                    <TextField type="number" label="Neutral starts at" value={pairingConfig.thresholds.neutral} disabled={readOnly || savingPairingConfig} inputProps={{ min: 0, max: 100, step: 1 }} onChange={(event) => updateThreshold('neutral', event.target.value)} helperText="Scores below this are Poor." />
                    <TextField type="number" label="Good starts at" value={pairingConfig.thresholds.good} disabled={readOnly || savingPairingConfig} inputProps={{ min: 0, max: 100, step: 1 }} onChange={(event) => updateThreshold('good', event.target.value)} helperText="Example: 60 makes 60+ Good." />
                    <TextField type="number" label="Excellent starts at" value={pairingConfig.thresholds.excellent} disabled={readOnly || savingPairingConfig} inputProps={{ min: 0, max: 100, step: 1 }} onChange={(event) => updateThreshold('excellent', event.target.value)} helperText="Top compatibility tier." />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Component Weights</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Weights are relative. Current total: {pairingCompatibilityWeightTotal(pairingConfig)}.
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                    {weightFields.map((field) => (
                      <TextField
                        key={field.key}
                        type="number"
                        label={field.label}
                        value={pairingConfig.weights[field.key]}
                        disabled={readOnly || savingPairingConfig}
                        inputProps={{ min: 0, max: 100, step: 1 }}
                        onChange={(event) => updateWeight(field.key, event.target.value)}
                        helperText={field.helper}
                      />
                    ))}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Minimum Evidence</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                    <TextField type="number" label="Minimum matches" value={pairingConfig.evidenceThresholds.minimumMatches} disabled={readOnly || savingPairingConfig} inputProps={{ min: 1, max: 20, step: 1 }} onChange={(event) => updateEvidence('minimumMatches', event.target.value)} />
                    <TextField type="number" label="Minimum regulation sets" value={pairingConfig.evidenceThresholds.minimumRegulationSets} disabled={readOnly || savingPairingConfig} inputProps={{ min: 0, max: 60, step: 1 }} onChange={(event) => updateEvidence('minimumRegulationSets', event.target.value)} />
                    <TextField type="number" label="Minimum matches with scores" value={pairingConfig.evidenceThresholds.minimumMatchesWithScores} disabled={readOnly || savingPairingConfig} inputProps={{ min: 0, max: 20, step: 1 }} onChange={(event) => updateEvidence('minimumMatchesWithScores', event.target.value)} />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Season Weighting</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                    <TextField type="number" label="Current season weight" value={pairingConfig.currentSeasonWeight} disabled={readOnly || savingPairingConfig} inputProps={{ min: 0, max: 3, step: 0.05 }} onChange={(event) => updateSeasonWeight('currentSeasonWeight', event.target.value)} />
                    <TextField type="number" label="Prior season weight" value={pairingConfig.priorSeasonWeight} disabled={readOnly || savingPairingConfig} inputProps={{ min: 0, max: 3, step: 0.05 }} onChange={(event) => updateSeasonWeight('priorSeasonWeight', event.target.value)} />
                    <TextField type="number" label="Older season weight" value={pairingConfig.olderSeasonWeight} disabled={readOnly || savingPairingConfig} inputProps={{ min: 0, max: 3, step: 0.05 }} onChange={(event) => updateSeasonWeight('olderSeasonWeight', event.target.value)} />
                  </Box>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="contained" startIcon={<SaveIcon />} onClick={() => void savePairingConfig()} disabled={readOnly || savingPairingConfig}>
                    Save Pairing Settings
                  </Button>
                  <Button startIcon={<RestoreIcon />} onClick={() => setPairingConfig(defaultPairingCompatibilityConfig)} disabled={readOnly || savingPairingConfig}>
                    Reset to Defaults
                  </Button>
                </Stack>
              </>
            )}
            <Divider />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Recalculate Compatibility Scores</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Recalculate data-driven partner compatibility ratings using completed match history. Captain-entered partner preferences and overrides are not affected.
              </Typography>
            </Box>
            <Alert severity="info">
              Pairings are evaluated across eligible completed or submitted match history. Defaulted and walkover lines are skipped because those matches were not played.
            </Alert>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={suppressRecalculationNotifications}
                  onChange={(event) => setSuppressRecalculationNotifications(event.target.checked)}
                  disabled={readOnly || recalculatingCompatibility}
                />
              )}
              label="Suppress Command Center alerts while recalculating"
            />
            {recalculatingCompatibility && <LinearProgress />}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<AutoFixHighIcon />}
                disabled={readOnly || recalculatingCompatibility || activeCompatibilityWorkspaces.length === 0}
                onClick={() => setRecalculationConfirmOpen(true)}
              >
                Recalculate Compatibility Scores
              </Button>
              <Typography variant="body2" color="text.secondary">
                Scope: {recalculationScopeText}.
              </Typography>
            </Box>
            {lastRecalculatedAt && recalculationSummary && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Last recalculated: {formatDateTime(lastRecalculatedAt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {recalculationSummary.ratingsEstablished + recalculationSummary.ratingsUpdated} pairing{recalculationSummary.ratingsEstablished + recalculationSummary.ratingsUpdated === 1 ? '' : 's'} updated from {recalculationSummary.diagnostics.eligibleMatches} completed match{recalculationSummary.diagnostics.eligibleMatches === 1 ? '' : 'es'}.
                </Typography>
              </Box>
            )}
            {recalculationSummary && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Recalculation Results</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                  <Metric label="Completed doubles lines" value={recalculationSummary.completedDoublesLines} />
                  <Metric label="Pairings evaluated" value={recalculationSummary.pairingsEvaluated} />
                  <Metric label="Ratings established" value={recalculationSummary.ratingsEstablished} />
                  <Metric label="Ratings updated" value={recalculationSummary.ratingsUpdated} />
                  <Metric label="Needs review" value={recalculationSummary.reviewFlags} />
                  <Metric label="Skipped: not enough evidence" value={recalculationSummary.skippedInsufficientEvidence} />
                  <Metric label="Incomplete score lines" value={recalculationSummary.incompleteScoreLines} />
                  <Metric label="Errors" value={recalculationSummary.errors.length} />
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 2, mb: 1 }}>Recalculation Diagnostics</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
                  <Metric label="Workspace teams" value={recalculationSummary.diagnostics.workspaceTeams} />
                  <Metric label="Candidate matches" value={recalculationSummary.diagnostics.candidateMatches} />
                  <Metric label="Match results loaded" value={recalculationSummary.diagnostics.matchResultsLoaded} />
                  <Metric label="Eligible matches" value={recalculationSummary.diagnostics.eligibleMatches} />
                  <Metric label="Doubles positions" value={recalculationSummary.diagnostics.doublesPositionsLoaded} />
                  <Metric label="Line results loaded" value={recalculationSummary.diagnostics.lineResultsLoaded} />
                  <Metric label="Lineup entries loaded" value={recalculationSummary.diagnostics.lineupEntriesLoaded} />
                  <Metric label="Playable doubles lines" value={recalculationSummary.diagnostics.playableDoublesLines} />
                  <Metric label="Defaults skipped" value={recalculationSummary.diagnostics.defaultedLinesSkipped} />
                  <Metric label="Lines missing 2 players" value={recalculationSummary.diagnostics.linesWithoutTwoPlayers} />
                </Box>
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      {isAdmin && !pairingCompatibilityEnabled && (
        <Paper variant="outlined" sx={{ p: 2, maxWidth: 920 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Partner Compatibility</Typography>
          <Alert severity="info" sx={{ mt: 1.5 }}>Pairing compatibility analytics are disabled in this environment.</Alert>
        </Paper>
      )}

      {isAdmin && (
        <Paper variant="outlined" sx={{ p: 2, maxWidth: 720 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Data Management</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Download a ZIP containing CSV exports of CourtCaptain data. This personal export complements, but does not replace, a full PostgreSQL backup.
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Button
                variant="contained"
                startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                onClick={() => void downloadBackup()}
                disabled={exporting}
              >
                {exporting ? 'Creating Backup…' : 'Download Backup ZIP'}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {formatLastBackup(lastBackupAt)}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      <Dialog open={recalculationConfirmOpen} onClose={() => setRecalculationConfirmOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Recalculate compatibility scores?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will recalculate data-driven partner compatibility ratings for {recalculationScopeText} using eligible completed match history. Captain-entered overrides will not be changed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecalculationConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void runCompatibilityRecalculation()} disabled={readOnly || recalculatingCompatibility || activeCompatibilityWorkspaces.length === 0}>
            Recalculate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
